import {
  getAllIds,
  getCountByStatus,
  Tirage,
  useGetTiragesByArtwork,
} from "@/api/tirages/tirage.api";
import NewItemDialogTrigger from "@/components/modal/NewItemDialogTrigger";
import StatusTableTab from "@/components/StatusTableTab";
import { AdvancedSearchChip } from "@/components/table/AdvancedSearchChip";
import { AdvancedSort } from "@/components/table/AdvancedSort";
import { GlobalSearch } from "@/components/table/GlobalSearch";
import NewTirageDialog from "@/components/tirage/NewTirageDialog";
import TirageSheetForm from "@/components/tirage/TirageSheetForm";
import { DataTable } from "@/components/ui/datatable";
import { ColumnFormatted, generateTable } from "@/config/dashboard/general";
import {
  tiragesAdvancedSearchConfig,
  tiragesColumns,
} from "@/config/dashboard/tirages";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const STORAGE_KEY = "tirage-table-preferences";

const flatVisibleTirageColumns = tiragesColumns.flatMap(
  (column: ColumnFormatted) => (column.visible ? column.key : []),
);

const TirageList = ({ artworkId }: { artworkId: string }) => {
  const { t } = useTranslation();

  const {
    pagination,
    setPagination,
    countByStatus,
    currentCount,
    fetchParams,
    filterStatus,
    setFilterStatus,
  } = useDashboardParams<Tirage>({
    resourceName: "tirage",
    resourceCounter: (where) => getCountByStatus(artworkId, where),
    statusField: "tiragePublicationStatus",
  });

  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectedTirageId, setSelectedTirageId] = useState<
    string | undefined
  >();
  const [initialTirageData, setInitialTirageData] = useState<
    Partial<Tirage> | undefined
  >();

  const handleOpenTirageSheet = (tirage: { id: string; tirageDescriptionNumeroEnregistrement?: string }) => {
    setSelectedTirageId(tirage.id);
    setInitialTirageData({
      tirageDescriptionNumeroEnregistrement: tirage.tirageDescriptionNumeroEnregistrement,
    });
    setSheetOpen(true);
  };

  const defaultOrder = useMemo(
    () => [{ tirageDescriptionNumeroEnregistrement: 'asc' as const }],
    [],
  );

  const orderWithDefault = useMemo(
    () => fetchParams.order && fetchParams.order.length > 0 
      ? fetchParams.order 
      : defaultOrder,
    [fetchParams.order, defaultOrder],
  );

  const { data } = useGetTiragesByArtwork({
    artworkId: artworkId,
    take: fetchParams.take,
    skip: fetchParams.skip,
    where: fetchParams.where,
    order: orderWithDefault,
  });

  const columns: ColumnDef<Tirage, any>[] = useMemo(() => {
    return generateTable(tiragesColumns, "tirage");
  }, []);

  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const { order } = JSON.parse(saved);
      return order || flatVisibleTirageColumns;
    }
    return flatVisibleTirageColumns;
  });

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const { visibility } = JSON.parse(saved);
        return (
          visibility || {
            ...tiragesColumns.reduce(
              (acc, column) => ({
                ...acc,
                [column.key]: column.visible || false,
              }),
              {},
            ),
          }
        );
      }
      return {
        ...tiragesColumns.reduce(
          (acc, column) => ({
            ...acc,
            [column.key]: column.visible || false,
          }),
          {},
        ),
      };
    },
  );

  const [selectionState, setSelectionState] = useState({});

  return (
    <div className="flex flex-col items-start gap-3">
      <div className="flex justify-between  items-center w-full">
        <p className="text-[24px] font-[600]">{t("dashboardTitleTirages")}</p>

        <GlobalSearch config={tiragesAdvancedSearchConfig} data={data ?? []} />
      </div>
      <div className="flex flex-row justify-between w-full">
        <StatusTableTab
          filterStatus={filterStatus}
          countByStatus={countByStatus}
          onValueChange={setFilterStatus}
        />
        <div className="flex flex-row gap-2">
          <AdvancedSort config={tiragesAdvancedSearchConfig} />
          <NewItemDialogTrigger
            title={t("createTirage")}
            Dialog={(props) => (
              <NewTirageDialog
                {...props}
                artworkId={artworkId}
                onCreateTirage={handleOpenTirageSheet}
              />
            )}
          />
        </div>
      </div>
      <div className="w-full flex flex-row">
        <AdvancedSearchChip />
      </div>
      <div className="w-full">
        <DataTable
          onRowClick={(row) => {
            setSelectedTirageId(row.original.id);
            setInitialTirageData(undefined);
            setSheetOpen(true);
          }}
          pagination={{
            pagination: pagination,
            setPage: setPagination,
            totalSize: currentCount,
          }}
          columns={columns}
          data={data ?? []}
          storageKey={STORAGE_KEY}
          options={{
            enableColumnResizing: true,
            state: {
              columnVisibility: columnVisibility,
              columnOrder: columnsOrder,
            },
            onColumnVisibilityChange: setColumnVisibility,
            onColumnOrderChange: setColumnsOrder,
          }}
          selection={{
            getRowId: (p) => p.id,
            getAllRowIds: () => getAllIds(artworkId, fetchParams.where),
            state: {
              selectionState,
              setSelectionState,
            },
            enableSelectAll: true,
          }}
        />
      </div>
      {selectedTirageId && (
        <TirageSheetForm
          open={sheetOpen}
          onOpenChange={(open) => {
            setSheetOpen(open);
            if (!open) {
              setSelectedTirageId(undefined);
              setInitialTirageData(undefined);
            }
          }}
          tirageId={selectedTirageId}
          artworkId={artworkId}
          initialData={initialTirageData}
        />
      )}
    </div>
  );
};

export default TirageList;
